'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import type {
  CardAlignment,
  CardIconSize,
  CardPadding,
  FlexibleContentCardResolved,
} from './types';
import { Button } from '@/components/Button';
import { Icon, IconType } from '@/components/Icon';
import { PlzFinder } from '@/components/PlzFinder';
import { SlotRenderer } from '@/components/SlotRenderer';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { type HeadingLevel, headingTag } from '@/lib/render/headingTag';
import { imageAlt } from '@/lib/render/imageAlt';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';
import { Text } from '@/lib/render/Text';
import { getSectionColorClass } from '@/lib/section-colors';
import type { SectionBackgroundColor } from '@/lib/section-colors';
import { toLiveHref } from '@/lib/toLiveHref';
import { toEmbedUrl } from '@/lib/videoEmbed';

// Card title typography. `3` matches the Navigation Grid card reference
// (`text-2xl font-medium`); `2` and `4` step around it.
const TITLE_CLASS: Record<HeadingLevel, string> = {
  '2': 'text-3xl font-medium',
  '3': 'text-2xl font-medium',
  '4': 'text-xl font-medium',
  '5': 'text-lg font-medium',
};

// Container-level icon size. `medium` is the historical 50px/32px pair from
// the layout metrics in 10.03.03.24; `small` is measured off the legacy site,
// `large` steps above. Kept in sync with the `iconSize` tables in 10.03.03.23
// and 10.03.03.24 — change both together.
const ICON_PX: Record<CardIconSize, { desktop: number; mobile: number }> = {
  small: { desktop: 24, mobile: 22 },
  medium: { desktop: 50, mobile: 32 },
  large: { desktop: 72, mobile: 48 },
};

// Container-level card padding. `small` is the measured 16px/12px reference
// from the live `.grid-colorbox`. Only applied when a background is set.
const PADDING_CLASS: Record<CardPadding, string> = {
  small: 'px-3 py-4',
  medium: 'px-4 py-5 md:px-5 md:py-6',
  large: 'px-7 py-9 md:px-10 md:py-12',
};

// Links render in brand blue and are never underlined; hovering swaps them to
// the primary text colour. Measured off the live Vorteile card, where inline
// links are #0050a0 with `text-decoration: none`.
// Editor-chosen alignment for the card's rich `text` field only. Everything
// else in the card (kicker, title, media, CTA) stays centred.
const TEXT_ALIGN_CLASS: Record<CardAlignment, string> = {
  left: 'text-center md:text-left',
  center: 'text-center',
  right: 'text-center md:text-right',
};

const LINK_CLASS = 'text-primary hover:text-secondary hover:underline';

interface CardProps {
  card: FlexibleContentCardResolved;
  background: SectionBackgroundColor | null;
  /** Container-level resolved phone (widget → location → domain), from the backend. */
  fallbackPhone?: string;
  /** Container-level resolved Matelso label, hard-paired with fallbackPhone. */
  fallbackMatelsoPhoneLabel?: string;
  /** Container-level icon size, shared by every card of the grid. */
  iconSize?: CardIconSize;
  /** Container-level card padding; only visible with a background. */
  padding?: CardPadding;
  /**
   * Classes the container puts on the card's own root — the grid's clamped
   * `md:col-span-*`. It belongs on the root rather than on a wrapper: a
   * wrapper would become the grid item and the card's `h-full` would then
   * measure against the wrapper's content height instead of the row.
   */
  className?: string;
}

// The YouTube and Vimeo players show their own poster frame before playback,
// so the card embeds directly rather than putting a click-to-play facade in
// front of it (that pattern belongs to the Video Recommendations widget, which
// has an editor-uploaded thumbnail of its own). `loading="lazy"` keeps the
// iframe off the critical path for cards below the fold.
function CardVideo({ url, title }: { url: string; title: string }) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black/5">
      <iframe
        src={toEmbedUrl(url)}
        title={title}
        loading="lazy"
        // Vimeo checks the `fullscreen` permission; `allowFullScreen` alone
        // only covers YouTube.
        allow="encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        className="h-full w-full"
      />
    </div>
  );
}

interface CtaProps {
  card: FlexibleContentCardResolved;
  fallbackPhone?: string;
  fallbackMatelsoPhoneLabel?: string;
  sub: (template: string) => string;
}

// CTA target and phone logic mirrors `ActionButton` in
// `src/widgets/cta-block/CtaBlockComponent.tsx`: an explicit link wins, the
// card's own (ctaPhone, ctaMatelsoPhoneLabel) is a hard pair that never mixes
// with the container's, and with no phone at all nothing is rendered
// (see 70.07-Phone-Number-Resolution).
function CardCta({
  card,
  fallbackPhone,
  fallbackMatelsoPhoneLabel,
  sub,
}: CtaProps) {
  const label = card.ctaLabel ? sub(card.ctaLabel) : undefined;

  let href: string;
  let displayLabel: string;
  let ariaLabel: string | undefined;
  let onClick: () => void;
  let isPhone = false;

  if (card.ctaLink) {
    // An explicit link whose placeholder did not resolve renders nothing — it
    // must not fall through to the phone branch below and dial instead.
    const link = substituteHref(card.ctaLink, sub);
    if (!link) {
      return null;
    }
    displayLabel = label || link;
    href = link;
    onClick = () =>
      pushCmsEventTracking(card.tracking, { label: displayLabel });
  } else {
    const phone = card.ctaPhone ? sub(card.ctaPhone) : fallbackPhone;
    if (!phone) {
      return null;
    }
    const matelsoPhoneLabel = card.ctaPhone
      ? card.ctaMatelsoPhoneLabel
        ? sub(card.ctaMatelsoPhoneLabel)
        : undefined
      : fallbackMatelsoPhoneLabel;

    displayLabel = label || matelsoPhoneLabel || phone;
    href = `tel:${phone.replace(/[^+\d]/g, '')}`;
    ariaLabel = `Anrufen: ${displayLabel}`;
    isPhone = true;
    onClick = () =>
      pushCmsEventTracking(card.tracking, { label: displayLabel, phone });
  }

  if (card.ctaStyle === 'primary' || card.ctaStyle === 'secondary') {
    return (
      <Button
        href={href}
        variant={card.ctaStyle}
        size="lg"
        className="mt-4 mb-4 self-center"
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {displayLabel}
      </Button>
    );
  }

  // A resolved phone number gets its own treatment regardless of which text
  // style is configured: a dark handset icon followed by the number in brand
  // blue. Measured off the live `span.text-phone` (Alle Fächer card) — the
  // FontAwesome handset is #28190a with a 3px gap, the number #0050a0 at
  // regular weight, never underlined at rest. Button styles keep their button.
  if (isPhone) {
    return (
      <a
        href={href}
        className="text-primary hover:text-secondary mt-3 inline-flex items-center gap-1 hover:underline md:mt-4"
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <Icon
          src={IconType.SvgPhoneFilled}
          size={16}
          className="text-secondary shrink-0"
        />
        {displayLabel}
      </a>
    );
  }

  // `arrowlink` is the live design's "➔ Label" text CTA.
  if (card.ctaStyle === 'arrowlink') {
    return (
      <a
        href={toLiveHref(href)}
        className="text-secondary hover:text-primary mt-3 inline-flex items-center gap-2 font-medium md:mt-4"
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <Icon src={IconType.SvgArrowRightBold} size={16} className="shrink-0" />
        {displayLabel}
      </a>
    );
  }

  // `headline` reuses the card's own title typography, `textlink` the
  // Navigation Grid's in-card link treatment collapsed into one responsive
  // anchor.
  const className =
    card.ctaStyle === 'headline'
      ? classNames('mt-4 block', TITLE_CLASS[card.titlePower], LINK_CLASS)
      : 'text-primary hover:text-secondary mt-3 inline-flex items-center font-medium md:mt-4 md:block md:text-lg';

  return (
    <a
      href={toLiveHref(href)}
      className={className}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {displayLabel}
    </a>
  );
}

export function FlexibleContentCard({
  card,
  background,
  fallbackPhone,
  fallbackMatelsoPhoneLabel,
  iconSize = 'medium',
  padding = 'small',
  className,
}: CardProps) {
  const { imageUrl, substitutePlaceholders: sub } = useRenderContext();

  const imgSrc = card.image ? imageUrl(card.image, { format: 'medium' }) : '';
  // Editors maintain the picture's alt text on the media itself; it may
  // carry a placeholder like any other editor-typed string.
  const imgAlt = sub(imageAlt(card.image));
  const hasIcon = Boolean(card.iconDesktop || card.iconMobile);
  // A CTA also renders when the card carries no phone of its own but the
  // container resolved one for it (70.07), so the fallback counts as content.
  const hasCta = Boolean(card.ctaLink || card.ctaPhone || fallbackPhone);
  const isEmpty =
    !card.kicker &&
    !card.title &&
    !card.videoUrl &&
    !imgSrc &&
    !hasIcon &&
    !card.text &&
    !hasCta &&
    !card.hasPlzFinder &&
    card.expandable.length === 0;

  if (isEmpty) {
    return null;
  }

  const { as: Heading, className: headingClass } = headingTag(
    card.titlePower,
    card.visualOnly,
  );

  // Eyebrow line above the title. Same treatment as the Title Widget's kicker
  // (`TextContentWidgetComponent`), so the two read identically on a page.
  const kicker = card.kicker ? (
    <Text
      as="p"
      field="kicker"
      className="text-primary mb-0 text-sm font-bold tracking-[0.10em] uppercase md:text-base"
    >
      {card.kicker}
    </Text>
  ) : null;

  const titleHref = substituteHref(card.titleLink, sub);
  const title = card.title ? (
    <Heading
      className={classNames(headingClass, 'mb-3', TITLE_CLASS[card.titlePower])}
    >
      {titleHref ? (
        <a href={toLiveHref(titleHref)} className={LINK_CLASS}>
          <Text as="span" field="title">
            {card.title}
          </Text>
        </a>
      ) : (
        <Text as="span" field="title">
          {card.title}
        </Text>
      )}
    </Heading>
  ) : null;

  const video = card.videoUrl ? (
    <CardVideo url={card.videoUrl} title={card.title ?? 'Video'} />
  ) : null;

  const media =
    imgSrc || hasIcon ? (
      <>
        {imgSrc &&
          (card.imageShape === 'circle' ? (
            // Portrait/avatar treatment: a centred square box that crops the
            // image to a circle, capped at the 276px of the Teachers widget's
            // avatar (`TeacherCard.tsx`) so a full-width card does not blow it
            // up. `object-cover` keeps faces centred whatever the upload's
            // aspect ratio.
            <div className="mx-auto mb-1 aspect-square w-full max-w-[276px] overflow-hidden rounded-full">
              <Image
                src={imgSrc}
                alt={imgAlt}
                width={card.image?.width ?? 552}
                height={card.image?.height ?? 552}
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <Image
              src={imgSrc}
              alt={imgAlt}
              width={card.image?.width ?? 1200}
              height={card.image?.height ?? 675}
              className="mb-1 h-auto w-full rounded-2xl"
            />
          ))}
        {hasIcon && (
          <>
            {card.iconDesktop && (
              <Icon
                src={card.iconDesktop}
                size={ICON_PX[iconSize].desktop}
                className="text-secondary-light hidden md:block"
              />
            )}
            {card.iconMobile && (
              <Icon
                src={card.iconMobile}
                size={ICON_PX[iconSize].mobile}
                className="text-secondary-light md:hidden"
              />
            )}
          </>
        )}
      </>
    ) : null;

  const body = (
    <>
      {card.text && (
        <div
          className={classNames(
            'benefits-checkmarks [&_a]:text-primary [&_a:hover]:text-secondary w-full [&_a:hover]:underline [&_li]:my-0 [&_p:last-child]:mb-0 [&_strong]:font-medium',
            TEXT_ALIGN_CLASS[card.alignment],
          )}
        >
          <RichText nodes={card.text} />
        </div>
      )}
      <CardCta
        card={card}
        fallbackPhone={fallbackPhone}
        fallbackMatelsoPhoneLabel={fallbackMatelsoPhoneLabel}
        sub={sub}
      />
      {/* The finder is a form and the panel a disclosure holding arbitrary
          snippet content — both need the full card width and read wrong
          centred, so they opt out of the card's `text-center` (SH-419). */}
      {card.hasPlzFinder && (
        <div className="w-full text-left">
          <PlzFinder withIntro={false} layout="split" />
        </div>
      )}
      {card.expandable.length > 0 && (
        <div className="w-full text-left">
          <SlotRenderer blocks={card.expandable} zoneName="expandable" />
        </div>
      )}
    </>
  );

  // Vertical rhythm: a uniform 12px base gap between every direct child (the
  // `media` and `body` fragments flatten into it), on top of which individual
  // elements add their own margin to reach the § Layout metrics numbers of
  // 10.03.03.24 — image `mb-1` → 16px to the heading, heading `mb-3` → 24px to
  // the body text, CTA `mt-3 md:mt-4` → 24/28px below the body text. The base
  // gap alone covers the elements the spec does not name (kicker, video, icon).
  const rootClass = classNames(
    'flex h-full flex-col gap-y-3',
    // The card itself is always centred; only the rich `text` field follows
    // the editor's `alignment`. Vertically the content stays at the top, per
    // AC 1 of 10.03.03.24 (a stretched card must not float its content in the
    // middle of the row).
    'items-center text-center',
    background ? classNames('rounded-2xl', PADDING_CLASS[padding]) : undefined,
    background ? getSectionColorClass(background) : undefined,
    className,
  );

  if (card.mediaPlacement === 'left') {
    return (
      <div className={rootClass}>
        {/* The text column repeats the root's `flex flex-col items-center
            gap-y-3`: as a plain block it would get no base gap at all and its
            children's margins would collapse into each other, so the vertical
            rhythm of § Layout metrics only holds if this column is a flex
            column too. `gap-y-3` on the wrapper spaces media against text
            while the two stack below `md`; `md:gap-4` takes over from `md`. */}
        <div className="flex flex-col gap-y-3 md:grid md:grid-cols-3 md:gap-4">
          <div className="md:col-span-1">{media}</div>
          <div className="flex flex-col items-center gap-y-3 md:col-span-2">
            {kicker}
            {title}
            {video}
            {body}
          </div>
        </div>
      </div>
    );
  }

  if (card.mediaPlacement === 'top') {
    return (
      <div className={rootClass}>
        {media}
        {kicker}
        {title}
        {video}
        {body}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {kicker}
      {title}
      {video}
      {media}
      {body}
    </div>
  );
}
